import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import Select from 'react-select';
import PropertyRequestReview from './PropertyRequestReview';
import BookingRequestReview from './BookingRequestReview';
import EditRequestReview from './EditRequestReview';
import { formatDubai } from '../../../utils/dubaiTime';
import CancelRequestReview from './CancelRequestReview';
import RegistrationReview from './RegistrationReview';
import BillingProfileRequestReview from './BillingProfileRequestReview';
import { useNotification } from '../../SharedView/contexts/NotificationContext';
import RequestRow, { TYPE_META, requestEntity } from './RequestRow';
import './pendingClientRequests.css';
import './requestsHistory.css';

const STATUS_META = {
  pending: { label: 'Pending', cls: 'rqh-chip--pending' },
  approved: { label: 'Approved', cls: 'rqh-chip--approved' },
  rejected: { label: 'Rejected', cls: 'rqh-chip--rejected' },
  cancelled: { label: 'Withdrawn', cls: 'rqh-chip--cancelled' },
};

// Pending first — the whole point is that unhandled requests are the first thing
// the admin sees (2026-07-16).
const STATUS_TABS = ['pending', 'all', 'approved', 'rejected', 'cancelled'];
const PAGE_SIZE = 50;

// Asia/Dubai render (item 8) — same "19 Jul 26, 14:30" visual as before.
const fmtDateTime = (iso) => (iso ? formatDubai(iso, 'dd MMM yy, HH:mm') : '');

const fullName = (first, last) => [first, last].filter(Boolean).join(' ').trim();

// Admin path for a request's entity. Bookings/properties/clients all have admin
// detail routes, so every entity a request references is reachable.
function adminEntityPath(ent) {
  if (!ent) return null;
  if (ent.kind === 'booking') return `/bookings/${ent.id}`;
  if (ent.kind === 'property') return `/properties/${ent.id}`;
  if (ent.kind === 'client') return `/clients/${ent.id}`;
  return null;
}

// The one dropdown: what to show first, then the individual request types.
const TYPE_OPTIONS = [
  {
    label: 'Show',
    options: [
      { value: 'all', label: 'All types' },
      { value: 'requests', label: 'Requests only · no top-ups' },
      { value: 'deposits', label: 'Wallet top-ups only' },
    ],
  },
  {
    label: 'Request types',
    options: Object.entries(TYPE_META).map(([k, m]) => ({ value: k, label: m.label })),
  },
];

export default function RequestsHistory() {
  const navigate = useNavigate();
  const { refreshPendingRequestCount } = useNotification();
  const [status, setStatus] = useState('pending');
  // ONE type filter (owner, 2026-09-07: "why is the deposit a separate tag
  // button instead of part of the dropdown like everything else"). Pending
  // top-ups are not request types — they come from the wallet queue and are
  // merged in — which is why they used to have their own "No deposits" pill.
  // The dropdown now carries them: 'all' = requests + top-ups (the queue as
  // the Home panel shows it), 'requests' = every request and no top-ups,
  // 'deposits' = top-ups only, anything else = that one request type.
  const [type, setType] = useState('all');
  const showsDeposits = type === 'all' || type === 'deposits';
  const showsRequests = type !== 'deposits';
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reviewing, setReviewing] = useState(null);
  const [reviewLoadingId, setReviewLoadingId] = useState(null);
  const [banner, setBanner] = useState('');

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    // Pending deposits ride along on the pending/all tabs (first page) so this
    // queue mirrors the Home panel — a client's top-up and their request sit
    // together. Card deposits are excluded server-side. A pending top-up has
    // no reviewed history, so the other status tabs never show any.
    const wantDeposits = showsDeposits
      && (status === 'pending' || status === 'all') && page === 1;
    try {
      const params = { page, limit: PAGE_SIZE };
      if (status !== 'all') params.status = status;
      if (type !== 'all' && type !== 'requests') params.type = type;
      const [histRes, depRes] = await Promise.allSettled([
        showsRequests
          ? axios.get('/api/client-change-requests/history', { params })
          : Promise.resolve({ data: { requests: [], total: 0 } }),
        wantDeposits ? axios.get('/api/wallet/deposits/pending') : Promise.resolve({ data: [] }),
      ]);
      if (histRes.status === 'fulfilled') {
        setRows(Array.isArray(histRes.value.data?.requests) ? histRes.value.data.requests : []);
        setTotal(Number(histRes.value.data?.total || 0));
      } else {
        throw histRes.reason;
      }
      // A deposit-fetch failure must not blank the request list.
      setDeposits(
        wantDeposits && depRes.status === 'fulfilled' && Array.isArray(depRes.value.data)
          ? depRes.value.data : [],
      );
    } catch (e) {
      if (e?.response?.status === 503) setError('The client-requests feature is currently disabled.');
      else setError('Failed to load request history.');
      setRows([]);
      setTotal(0);
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  }, [status, type, page, showsDeposits, showsRequests]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  // Reset to page 1 whenever a filter changes.
  useEffect(() => { setPage(1); }, [status, type]);

  // Merge requests + pending deposits into one list. Deposits are only present
  // on page 1 of the pending/all tabs, so they never duplicate across pages.
  // When there are no deposits we keep the server's ordering untouched (the
  // history endpoint sorts by reviewed_at/created_at DESC); only when deposits
  // are injected do we re-sort the combined list newest-first by created_at.
  const items = useMemo(() => {
    const reqItems = rows.map((r) => ({ kind: 'request', created_at: r.created_at, key: `req-${r.id}`, data: r }));
    if (deposits.length === 0) return reqItems;
    return [
      ...reqItems,
      ...deposits.map((d) => ({ kind: 'deposit', created_at: d.created_at, key: `dep-${d.id}`, data: d })),
    ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [rows, deposits]);

  // History rows omit original_snapshot / linked_deposit_* (see the /history
  // SELECT), so fetch the FULL request before opening the review modal. If it is
  // no longer pending (another admin beat us to it), surface that and refresh
  // instead of opening a modal that would only 409 on submit.
  const openReview = async (id) => {
    setReviewLoadingId(id);
    setBanner('');
    try {
      const res = await axios.get(`/api/client-change-requests/${id}`);
      const full = res.data?.request;
      if (!full) throw new Error('Request not found.');
      // Non-pending rows open READ-ONLY (2026-08-06).
      setReviewing(full);
    } catch (e) {
      setBanner(e?.response?.data?.error || 'Could not open this request for review.');
      setTimeout(() => setBanner(''), 6000);
    } finally {
      setReviewLoadingId(null);
    }
  };

  // Mirrors the Home panel: clear the modal, flash a banner, refetch the list,
  // and drop the sidebar badge immediately.
  const handleDone = (outcome, message, resultId) => {
    const type = reviewing?.request_type;
    setReviewing(null);
    fetchHistory();
    if (refreshPendingRequestCount) refreshPendingRequestCount();
    if (outcome === 'approved') {
      // Toast survives the redirect; jump to the created/updated entity.
      const DEFAULTS = {
        create_booking: 'Booking created.', edit_booking: 'Booking change applied.',
        cancel_booking: 'Cancellation applied.', create_property: 'Property created.',
        edit_property: 'Property change applied.', edit_client_profile: 'Profile change applied.',
        edit_client_tasks: 'Tasks updated.', add_billing_profile: 'Billing entity created.',
        edit_billing_profile: 'Billing entity updated.',
      };
      toast.success(message || DEFAULTS[type] || 'Request approved.');
      let dest = null;
      if (resultId) {
        if (['create_booking', 'edit_booking', 'cancel_booking'].includes(type)) dest = `/bookings/${resultId}`;
        else if (['create_property', 'edit_property'].includes(type)) dest = `/properties/${resultId}`;
      }
      if (dest) navigate(dest);
      return;
    }
    setBanner(message || 'Request rejected.');
    setTimeout(() => setBanner(''), 8000);
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="rqh">
      <div className="rqh__head">
        <h2 className="rqh__title">Client Requests</h2>
        <p className="rqh__sub">Every request and who reviewed it.</p>
      </div>

      <div className="rqh__filters">
        <div className="rqh__tabs">
          {STATUS_TABS.map((s) => (
            <button
              key={s}
              type="button"
              className={`rqh__tab${status === s ? ' rqh__tab--active' : ''}`}
              onClick={() => setStatus(s)}
            >
              {s === 'all' ? 'All' : STATUS_META[s].label}
            </button>
          ))}
        </div>
        <div className="rqh__filtersRight">
          <div className="rqh__typeSelect">
            <Select
              inputId="requests-type-filter"
              classNamePrefix="hhc-select"
              options={TYPE_OPTIONS}
              value={TYPE_OPTIONS.flatMap((g) => g.options).find((o) => o.value === type)}
              onChange={(opt) => setType(opt?.value ?? 'all')}
              isSearchable={false}
              menuPortalTarget={document.body}
              styles={{
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                container: (base) => ({ ...base, width: '100%' }),
              }}
            />
          </div>
        </div>
      </div>

      {/* Counts respect the filter: the request total is what the server
          returned for the current status/type, and pending top-ups are only
          counted when they are actually in the list. */}
      {!loading && !error && (
        <div className="rqh__count">
          {showsRequests && `${total} request${total === 1 ? '' : 's'}`}
          {showsRequests && showsDeposits && deposits.length > 0 && ' · '}
          {showsDeposits && (deposits.length > 0 || !showsRequests)
            && `${deposits.length} pending top-up${deposits.length === 1 ? '' : 's'}`}
          {type === 'requests' && ' · top-ups hidden'}
        </div>
      )}

      {!!banner && <div className="rqh__banner">{banner}</div>}

      {loading ? (
        <div className="rqh__empty">Loading…</div>
      ) : error ? (
        <div className="rqh__empty rqh__empty--error">{error}</div>
      ) : items.length === 0 ? (
        <div className="rqh__empty">No requests found.</div>
      ) : (
        <div className="rqh__list">
          {items.map((item) => {
            if (item.kind === 'deposit') {
              const dep = item.data;
              return (
                <RequestRow
                  key={item.key}
                  kind="deposit"
                  data={dep}
                  statusChip={{ label: 'Pending', className: 'pcr-status-chip--pending' }}
                  metaText={`Submitted · ${fmtDateTime(dep.created_at)}`}
                  onRowClick={() => navigate('/payments')}
                />
              );
            }

            const r = item.data;
            const st = STATUS_META[r.status] || { label: r.status, cls: '' };
            const reviewer = fullName(r.reviewed_by_first_name, r.reviewed_by_last_name);
            const requester = fullName(r.requested_by_first_name, r.requested_by_last_name);
            // WHOLE-ROW click (2026-07-21). Pending → open the review modal;
            // everything else → jump to the entity (Booking/Property/Client).
            const ent = requestEntity(r);
            // Approved non-LM cancellation deletes the booking, so its /bookings
            // page is gone — fall through to the client. Any other row with no
            // entity path also falls back to the client's page.
            const cancelGone = r.request_type === 'cancel_booking' && r.status === 'approved'
              && !r.payload?.is_last_minute_at_request;
            let navPath = cancelGone ? null : adminEntityPath(ent);
            if (!navPath && r.client_id) navPath = `/clients/${r.client_id}`;
            // Every row opens its review — non-pending read-only. The entity
            // page stays reachable from inside the modal / the entity chip.
            const onRowClick = () => openReview(r.id);
            return (
              <RequestRow
                key={item.key}
                kind="request"
                data={r}
                statusChip={{ label: st.label, className: `pcr-status-chip--${r.status}` }}
                entityRef={ent?.label}
                onRowClick={onRowClick}
                busy={reviewLoadingId === r.id}
                metaText={`Requested by ${requester || '—'} · ${fmtDateTime(r.created_at)}`}
                extraMeta={(
                  <>
                    {r.status !== 'pending' && r.status !== 'cancelled' && (
                      <span className="rqh__reviewer">
                        {' · '}{r.status === 'approved' ? 'Approved' : 'Rejected'} by <strong>{reviewer || '—'}</strong>
                        {r.reviewed_at ? ` · ${fmtDateTime(r.reviewed_at)}` : ''}
                      </span>
                    )}
                    {/* A cancelled row with review_notes is a payment-teardown
                        (card failed/cancelled/expired — routes/payments.js stamps
                        the reason); only a bare cancelled row is a genuine client
                        withdrawal. */}
                    {r.status === 'cancelled' && (
                      <span>{' · '}{r.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client'}</span>
                    )}
                  </>
                )}
                footer={(r.status === 'rejected' || r.status === 'cancelled') && r.review_notes ? (
                  <div className="rqh__reason"><strong>Reason:</strong> {r.review_notes}</div>
                ) : null}
              />
            );
          })}
        </div>
      )}

      {totalPages > 1 && (
        <div className="rqh__pagination">
          <button type="button" className="rqh__pageBtn" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>Previous</button>
          <span className="rqh__pageInfo">Page {page} of {totalPages} · {total} total</span>
          <button type="button" className="rqh__pageBtn" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))}>Next</button>
        </div>
      )}

      {reviewing && reviewing.request_type === 'create_property' && (
        <PropertyRequestReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06): "Open Booking / Open
            // Property / Open Client" inside the read-only modal — falls
            // back to the client page (an approved LM-cancel's booking is
            // deleted, so its page is gone).
            const ent = requestEntity(reviewing);
            const gone = reviewing.request_type === 'cancel_booking' && reviewing.status === 'approved'
              && !reviewing.payload?.is_last_minute_at_request;
            let path = gone ? null : adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
      {reviewing && reviewing.request_type === 'create_booking' && (
        <BookingRequestReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06): "Open Booking / Open
            // Property / Open Client" inside the read-only modal — falls
            // back to the client page (an approved LM-cancel's booking is
            // deleted, so its page is gone).
            const ent = requestEntity(reviewing);
            const gone = reviewing.request_type === 'cancel_booking' && reviewing.status === 'approved'
              && !reviewing.payload?.is_last_minute_at_request;
            let path = gone ? null : adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
      {reviewing && (reviewing.request_type === 'edit_booking'
        || reviewing.request_type === 'edit_property'
        || reviewing.request_type === 'edit_client_profile'
        || reviewing.request_type === 'edit_client_tasks') && (
        <EditRequestReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06): "Open Booking / Open
            // Property / Open Client" inside the read-only modal — falls
            // back to the client page (an approved LM-cancel's booking is
            // deleted, so its page is gone).
            const ent = requestEntity(reviewing);
            const gone = reviewing.request_type === 'cancel_booking' && reviewing.status === 'approved'
              && !reviewing.payload?.is_last_minute_at_request;
            let path = gone ? null : adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
      {reviewing && reviewing.request_type === 'cancel_booking' && (
        <CancelRequestReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06): "Open Booking / Open
            // Property / Open Client" inside the read-only modal — falls
            // back to the client page (an approved LM-cancel's booking is
            // deleted, so its page is gone).
            const ent = requestEntity(reviewing);
            const gone = reviewing.request_type === 'cancel_booking' && reviewing.status === 'approved'
              && !reviewing.payload?.is_last_minute_at_request;
            let path = gone ? null : adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
      {reviewing && reviewing.request_type === 'register_client' && (
        <RegistrationReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06): "Open Booking / Open
            // Property / Open Client" inside the read-only modal — falls
            // back to the client page (an approved LM-cancel's booking is
            // deleted, so its page is gone).
            const ent = requestEntity(reviewing);
            const gone = reviewing.request_type === 'cancel_booking' && reviewing.status === 'approved'
              && !reviewing.payload?.is_last_minute_at_request;
            let path = gone ? null : adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
      {reviewing && (reviewing.request_type === 'add_billing_profile'
        || reviewing.request_type === 'edit_billing_profile') && (
        <BillingProfileRequestReview
          readOnlyOutcome={reviewing.status !== 'pending' ? (() => {
            // Entity jump button (2026-08-06) — a billing-entity request
            // always points at its client, so that's the jump target.
            const ent = requestEntity(reviewing);
            let path = adminEntityPath(ent);
            let entityLabel = ent && path
              ? `Open ${ent.kind.charAt(0).toUpperCase()}${ent.kind.slice(1)}`
              : null;
            if (!path && reviewing.client_id) { path = `/clients/${reviewing.client_id}`; entityLabel = 'Open Client'; }
            return {
              statusLabel: reviewing.status === 'cancelled'
                ? (reviewing.review_notes ? 'Withdrawn automatically' : 'Withdrawn by client')
                : reviewing.status,
              reviewerName: [reviewing.reviewed_by_first_name, reviewing.reviewed_by_last_name].filter(Boolean).join(' ') || null,
              reviewedAt: reviewing.reviewed_at ? new Date(reviewing.reviewed_at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai' }) : null,
              reason: reviewing.review_notes || null,
              entityLabel,
              onOpenEntity: path ? () => navigate(path) : null,
            };
          })() : null}
          request={reviewing}
          onClose={() => setReviewing(null)}
          onDone={handleDone}
        />
      )}
    </div>
  );
}
